import { motion } from 'framer-motion'
import {
  Briefcase, MapPin, Clock, DollarSign, Heart, Coffee, Globe, Users,
  ArrowRight, Laptop, BookOpen, Sparkles, Loader2
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'
import { useApi } from '../hooks/useApi'
import { careersApi, type Career } from '../lib/api'

const benefitIcons = [DollarSign, Heart, Laptop, Coffee, BookOpen, Globe]

// `query` is the value the careers API filters by (not UI text); labels live in careers.json
const departments = [
  { id: 'all', query: undefined, count: 8 },
  { id: 'engineering', query: 'Mühendislik', count: 4 },
  { id: 'design', query: 'Tasarım', count: 1 },
  { id: 'marketing', query: 'Pazarlama', count: 2 },
  { id: 'operations', query: 'Operasyon', count: 1 },
] as const

type DepartmentId = (typeof departments)[number]['id']

function formatSalary(job: Career): string {
  if (job.salaryMin != null && job.salaryMax != null) {
    const currency = job.salaryCurrency === 'USD' ? '$' : job.salaryCurrency
    return `${currency}${(job.salaryMin / 1000).toFixed(0)}k - ${currency}${(job.salaryMax / 1000).toFixed(0)}k`
  }
  return ''
}

export default function Careers() {
  const { t } = useTranslation(['careers', 'common'])
  const [activeDepartment, setActiveDepartment] = useState<DepartmentId>('all')

  const benefitTexts = t('benefits.items', { returnObjects: true }) as { title: string; desc: string }[]
  const benefits = benefitTexts.map((b, i) => ({ icon: benefitIcons[i], ...b }))

  const activeQuery = departments.find((d) => d.id === activeDepartment)?.query

  const { data: jobs, loading } = useApi(
    () => careersApi.getAll(activeDepartment === 'all' ? undefined : activeQuery),
    [activeDepartment]
  )

  const filteredJobs = jobs || []

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/careers"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.careers'), url: '/careers' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Briefcase className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.titleStart')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">
            {t('benefits.title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass rounded-xl p-6 flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Jobs Section */}
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass rounded-2xl p-6 sticky top-24">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                {t('departments.title')}
              </h3>
              <div className="space-y-2">
                {departments.map((dept) => (
                  <button
                    key={dept.id}
                    onClick={() => setActiveDepartment(dept.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      activeDepartment === dept.id
                        ? 'bg-primary text-white'
                        : 'hover:bg-white/5 text-gray-400 hover:text-white'
                    }`}
                  >
                    <span>{t(`departments.labels.${dept.id}`)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      activeDepartment === dept.id ? 'bg-white/20' : 'bg-white/10'
                    }`}>
                      {dept.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Job Listings */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {activeDepartment === 'all'
                  ? t('jobs.allTitle')
                  : t('jobs.departmentTitle', { department: t(`departments.labels.${activeDepartment}`) })}
              </h2>
              <span className="text-gray-400 text-sm">{t('jobs.count', { count: filteredJobs.length })}</span>
            </div>

            {loading && (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            )}

            <div className="space-y-4">
              {!loading && filteredJobs.map((job) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="glass rounded-xl p-6 group hover:bg-white/10 transition-all cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors">
                          {job.title}
                        </h3>
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                          {job.department}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm mb-3">{job.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {formatSalary(job)}
                        </span>
                      </div>
                    </div>
                    <button className="flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-primary text-white rounded-lg transition-all group/btn">
                      {t('jobs.apply')}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {!loading && filteredJobs.length === 0 && (
              <div className="text-center py-20 glass rounded-2xl">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('jobs.emptyTitle')}</h3>
                <p className="text-gray-400">{t('jobs.emptyText')}</p>
              </div>
            )}
          </div>
        </div>

        {/* General Application */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 glass rounded-2xl p-8 md:p-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-medium">{t('general.eyebrow')}</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            {t('general.title')}
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            {t('general.text')}
          </p>
          <button className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all">
            {t('general.button')}
          </button>
        </motion.div>
      </div>
    </motion.div>
  )
}
